import ExcelJS from "exceljs";
import { convertToTitleCase, columnIndexToLetter } from "./common-tools";

// #region load image as buffer
async function loadImageAsBuffer(imagePath) {
  try {
    const response = await fetch(imagePath);
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.statusText}`);
    }
    return await response.arrayBuffer();
  } catch (error) {
    console.error('Error loading image:', error);
    return null;
  }
}


// #region single worksheet
/**
 * Exports data to an Excel file.
 * @param {Array} dataList - The data to export.
 * @param {Array} skipKeys - Keys to skip in the export.
 * @param {Array} headerKeys - Optional column key order. Falls back to Object.keys of the first row.
 * @param {Object} headerLabels - Optional map of key -> header text. Falls back to convertToTitleCase(key).
 * @param {String} fileName - The name of the exported file.
 * @param {String} worksheetName - Name of the worksheet in the template.
 * @param {String} title - Title to add to the Excel file.
 */

// generate for excel has single worksheet
export async function exportToExcel({
  dataList,
  skipKeys = [],
  headerKeys,
  headerLabels = {},
  fileName = "ExportedFile.xlsx",
  newWorksheetName = "Template_1",
  title = "Summary List",
}) {

  const workbook = new ExcelJS.Workbook();

  const worksheet = workbook.addWorksheet("Template_1");

  worksheet.name = newWorksheetName;

  // add logo
  try {
    const imageBuffer = await loadImageAsBuffer('/motion2.png');
    if (imageBuffer) {
      const imageId1 = workbook.addImage({
        buffer: imageBuffer,
        extension: 'png',
      });
      worksheet.addImage(imageId1, 'A1:A1');
    } else {
      console.warn('Could not load logo image from /motion.png');
    }
  } catch (error) {
    console.warn('Could not add logo to Excel:', error);
  }

  // Set the title in the worksheet
  const titleCell = worksheet.getCell("C1");
  titleCell.value = title;

  // Get the header keys, excluding the skip keys
  const sourceKeys = Array.isArray(headerKeys) && headerKeys.length > 0
    ? headerKeys
    : Object.keys(dataList[0] || {});
  const header = sourceKeys.filter((key) => !skipKeys.includes(key));

  // Add the header row
  header.forEach((key, index) => {
    const cell = worksheet.getCell(2, index + 1); // Assuming header is on row 2
    cell.value = headerLabels[key] || convertToTitleCase(key); // table label, or title-case the key
    cell.alignment = { 
      vertical: 'middle', 
      horizontal: 'center', 
      wrapText: false 
    };
    cell.font = {
      name: 'Calibri', // Font family
      family: 4, // Font family
      size: 12, // Font size
      bold: true, // Bold
      italic: false, // Italic
      underline: false, // Underline
      color: { argb: '0D3937' } // Text color
    };
    cell.fill = { 
      type: 'pattern', 
      pattern: 'solid', 
      fgColor: { argb: 'A9D08E' } };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    };
  });

  // Add the data rows
  const startRow = 3; // Data starts from row 3
  dataList.forEach((rowData, index) => {
    const rowNumber = startRow + index;
    const newRow = worksheet.getRow(rowNumber);

    // Determine fill color based on even/odd row
    const isEvenRow = (index + 1) % 2 === 0;
    const fillColor = isEvenRow ? 'E2EFDA' : 'FFFFFF';

    header.forEach((key, colOffset) => {
      const cell = newRow.getCell(colOffset + 1);
      cell.value = rowData[key];
      cell.font = {
        name: 'Calibri', // Font family
        family: 4, // Font family
        size: 11, // Font size
        bold: false, // Bold
        italic: false, // Italic
        underline: false, // Underline
        color: { argb: '00000000' } // Text color
      };
      cell.alignment = { 
        vertical: 'middle', 
        horizontal: 'left', 
        wrapText: false 
      };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fillColor } };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
    });

    newRow.commit();
  });



  // Delete columns after the last column of the data list
  const lastColumn = header.length; // Get the last column index (1-based)
  // const totalColumns = worksheet.columnCount; // Get the total number of columns in the worksheet
  // if (lastColumn < totalColumns) {
  //   worksheet.spliceColumns(lastColumn + 1, totalColumns - lastColumn);
  // }

  //     // Delete rows after the last row of the data list
  // const lastRow = dataList.length + startRow - 1;
    
  //     // Delete any rows beyond the last data row
  // for (let i = worksheet.rowCount; i > lastRow; i--) {
  //       worksheet.spliceRows(i, 1);
  // }

  // #region styling
  const lastColumnLetter = columnIndexToLetter(lastColumn);
  const mergeEnd = `${lastColumnLetter}1`;
  worksheet.mergeCells('C1:' + mergeEnd);
  worksheet.getCell('C1').alignment = { vertical: 'middle', horizontal: 'left' };
  worksheet.getCell('C1').font = {
    name: 'Calibri',
    color: { argb: 'FFFFFFFF' },
    family: 4,
    size: 36,
    underline: false,
    italic: false,
    bold: true
  };
  worksheet.getCell('A1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '141722' } };
  worksheet.getCell('B1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '141722' } };
  worksheet.getCell('C1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '141722' } };




  // Export the updated file
  const blob = await workbook.xlsx.writeBuffer();
  const url = window.URL.createObjectURL(new Blob([blob]));

  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  window.URL.revokeObjectURL(url);
}

// #endregion single worksheet

const CHARGER_LEVEL_EXPORT_HEADERS = [
  "Motion Site ID",
  "site_name",
  "postcode",
  "customer",
  "state",
  "elec_region",
  "site_role",
  "distance_m",
  "Motion Charger ID",
  "charger_name",
  "year_added",
  "decommissioning_date",
  "active_until",
  "included",
  "change_details",
  "change_date",
];

function addGroupByDistanceSheet(workbook, {
  imageId,
  worksheetName,
  title,
  dataList = [],
  skipKeys = [],
  headerKeys,
  getRowFill,
}) {
  const worksheet = workbook.addWorksheet(worksheetName);

  if (imageId != null) {
    worksheet.addImage(imageId, "A1:A1");
  }

  const titleCell = worksheet.getCell("C1");
  titleCell.value = title;

  const sourceKeys = Array.isArray(headerKeys) && headerKeys.length > 0
    ? headerKeys
    : (dataList[0] ? Object.keys(dataList[0]) : []);
  const header = sourceKeys.filter((key) => !skipKeys.includes(key));

  header.forEach((key, index) => {
    const cell = worksheet.getCell(2, index + 1);
    cell.value = convertToTitleCase(key);
    cell.alignment = {
      vertical: "middle",
      horizontal: "center",
      wrapText: false,
    };
    cell.font = {
      name: "Calibri",
      family: 4,
      size: 12,
      bold: true,
      italic: false,
      underline: false,
      color: { argb: "0D3937" },
    };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "A9D08E" },
    };
    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
  });

  const startRow = 3;
  dataList.forEach((rowData, index) => {
    const rowNumber = startRow + index;
    const newRow = worksheet.getRow(rowNumber);
    const fillColor = getRowFill ? getRowFill(rowData, index) : "FFFFFF";

    header.forEach((key, colOffset) => {
      const cell = newRow.getCell(colOffset + 1);
      cell.value = rowData[key];
      cell.font = {
        name: "Calibri",
        family: 4,
        size: 11,
        bold: false,
        italic: false,
        underline: false,
        color: { argb: "00000000" },
      };
      cell.alignment = {
        vertical: "middle",
        horizontal: "left",
        wrapText: false,
      };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: fillColor } };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    newRow.commit();
  });

  const lastColumn = Math.max(header.length, 3);
  const lastColumnLetter = columnIndexToLetter(lastColumn);
  worksheet.mergeCells("C1:" + lastColumnLetter + "1");
  worksheet.getCell("C1").alignment = { vertical: "middle", horizontal: "left" };
  worksheet.getCell("C1").font = {
    name: "Calibri",
    color: { argb: "FFFFFFFF" },
    family: 4,
    size: 36,
    underline: false,
    italic: false,
    bold: true,
  };
  worksheet.getCell("A1").fill = { type: "pattern", pattern: "solid", fgColor: { argb: "141722" } };
  worksheet.getCell("B1").fill = { type: "pattern", pattern: "solid", fgColor: { argb: "141722" } };
  worksheet.getCell("C1").fill = { type: "pattern", pattern: "solid", fgColor: { argb: "141722" } };
}

// Group Sites By distance excel
export async function exportGroupSitesByDistanceExcel({
  dataList,
  chargerDataList,
  skipKeys = [],
  fileName = "ExportedFile.xlsx",
  newWorksheetName = "Template_1",
  title = "Summary List",
}) {

  const workbook = new ExcelJS.Workbook();

  let imageId = null;
  try {
    const imageBuffer = await loadImageAsBuffer("/motion2.png");
    if (imageBuffer) {
      imageId = workbook.addImage({
        buffer: imageBuffer,
        extension: "png",
      });
    } else {
      console.warn("Could not load logo image from /motion.png");
    }
  } catch (error) {
    console.warn("Could not add logo to Excel:", error);
  }

  addGroupByDistanceSheet(workbook, {
    imageId,
    worksheetName: newWorksheetName,
    title,
    dataList: Array.isArray(dataList) ? dataList : [],
    skipKeys,
    getRowFill: (rowData) => (rowData.distance_m === "Central Site" ? "E2EFDA" : "FFFFFF"),
  });

  addGroupByDistanceSheet(workbook, {
    imageId,
    worksheetName: "Charger Level",
    title: `${title} - Chargers`,
    dataList: Array.isArray(chargerDataList) ? chargerDataList : [],
    skipKeys,
    headerKeys: CHARGER_LEVEL_EXPORT_HEADERS,
    getRowFill: (rowData) => (rowData.distance_m === "Central Site" ? "E2EFDA" : "FFFFFF"),
  });

  const blob = await workbook.xlsx.writeBuffer();
  const url = window.URL.createObjectURL(new Blob([blob]));

  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  window.URL.revokeObjectURL(url);
}

// #endregion single worksheet


// #region multiple worksheets
// This function exports multiple data lists to an Excel file using a template.
/**
 * Exports data to an Excel file.
 * @param {String} title - Main title of each sheet, worksheet title: title + "- table name".
 * @param {Object} tableData - Object with datalists of table data to export.
 * @param {Array} tableNames - A list of table Names used for name each worksheet.
 * @param {Array} skipKeys - List of Keys in datalist to skip the table column in the export.
 * @param {String} fileName - The name of the exported file.
 */
export async function exportMultipleDataLists({
  title,
  tableData,
  tableNames,
  skipKeysList = {}, // Object of skipKeys for each tableData key
  fileName = "AuditorReport.xlsx",
}) {
  
  const workbook = new ExcelJS.Workbook();

  Object.keys(tableData).forEach((key, index) => {
    const dataList = tableData[key];
    if(!Array.isArray(dataList) || dataList.length === 0) {
      console.log("skip empty list", key);
      return;
    }
    const templateWorksheetName = `Template_${index + 1}`; // Use template_1 to template_10
    const newWorksheetName = tableNames[key]; // Get the new worksheet name from tableNames
    
    if (!tableNames[key]) console.log("not found", key);
    
    // create new template sheet
    const worksheet = workbook.addWorksheet(templateWorksheetName);

    // change title of each worksheet
    const titleCell = worksheet.getCell("C1")
    titleCell.value = title + ` - ${newWorksheetName}`



    // Rename the worksheet to the new name
    worksheet.name = newWorksheetName;

    // add logo
    try {
      const imageBuffer = loadImageAsBuffer('/motion2.png');
      if (imageBuffer) {
        const imageId1 = workbook.addImage({
          buffer: imageBuffer,
          extension: 'png',
        });
        worksheet.addImage(imageId1, 'A1:A1');
      } else {
        console.warn('Could not load logo image from /motion.png');
      }
    } catch (error) {
      console.warn('Could not add logo to Excel:', error);
    }

    const skipKeys = skipKeysList[key] || []; // Use corresponding skipKeys or default to an empty array

    // Get the header keys, excluding the skip keys
    const header = Object.keys(dataList[0] || {}).filter((key) => !skipKeys.includes(key));

    // Add the header row
    const headerRow = worksheet.getRow(2); // Assuming header starts at row 2
    header.forEach((key, colIndex) => {
      const cell = headerRow.getCell(colIndex + 1); // Columns are 1-based
      cell.value = convertToTitleCase(key); // convert header

      // Header row styling
      cell.alignment = { 
        vertical: 'middle', 
        horizontal: 'center', 
        wrapText: false 
      };
      cell.font = {
        name: 'Calibri', // Font family
        family: 4, // Font family
        size: 12, // Font size
        bold: true, // Bold
        italic: false, // Italic
        underline: false, // Underline
        color: { argb: '0D3937' } // Text color
      };
      cell.fill = { 
        type: 'pattern', 
        pattern: 'solid', 
        fgColor: { argb: 'A9D08E' } };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
  
    });
    headerRow.commit();                                                                                                                                                                                                                                                                                             

    // Add the data rows
    const startRow = 3; // Data starts from row 3
    dataList.forEach((rowData, rowIndex) => {
      const row = worksheet.getRow(startRow + rowIndex);

          // Determine fill color based on even/odd row
    const isEvenRow = (rowIndex + 1) % 2 === 0;
    const fillColor = isEvenRow ? 'E2EFDA' : 'FFFFFF';

      header.forEach((key, colIndex) => {
        const cell = row.getCell(colIndex + 1);
        cell.value = rowData[key];

        // Apply fill color to the cell
        cell.font = {
          name: 'Calibri', // Font family
          family: 4, // Font family
          size: 11, // Font size
          bold: false, // Bold
          italic: false, // Italic
          underline: false, // Underline
          color: { argb: '00000000' } // Text color
        };
        cell.alignment = { 
          vertical: 'middle', 
          horizontal: 'left', 
          wrapText: false 
        };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fillColor } };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
  
      });
      row.commit();
    });

    // Delete columns after the last column of the data list
    const lastColumn = header.length; // Get the last column index (1-based)
    // const totalColumns = worksheet.columnCount; // Get the total number of columns in the worksheet
    // if (lastColumn < totalColumns) {
    //   worksheet.spliceColumns(lastColumn + 1, totalColumns - lastColumn);
    // }

    // // Delete rows after the last row of the data list
    // const lastRow = dataList.length + startRow - 1;
    
    // // Delete any rows beyond the last data row
    // for (let i = worksheet.rowCount; i > lastRow; i--) {
    //   worksheet.spliceRows(i, 1);
    // }

    // Merge cells for the Title row
    const lastColumnLetter = columnIndexToLetter(lastColumn);
    const mergeEnd = `${lastColumnLetter}1`;
    worksheet.mergeCells('C1:' + mergeEnd);
    worksheet.getCell('C1').alignment = { vertical: 'middle', horizontal: 'left' };
    worksheet.getCell('C1').font = {
      name: 'Calibri',
      color: { argb: 'FFFFFFFF' },
      family: 4,
      size: 36,
      underline: false,
      italic: false,
      bold: true
    };
    worksheet.getCell('A1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '141722' } };
    worksheet.getCell('B1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '141722' } };
    worksheet.getCell('C1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '141722' } };
  });

  // // remove Worksheets are not used
  // workbook.worksheets
  // .filter(ws => ws.name.startsWith("Template_"))
  // .forEach(ws => workbook.removeWorksheet(ws.id));

  await downloadWorkbook(workbook, fileName);
}

// #endregion multiple worksheets


// #region download workbook
// This function downloads the workbook as an Excel file with the specified file name.
export async function downloadWorkbook(workbook, fileName) {
  // Export the updated file
  const blob = await workbook.xlsx.writeBuffer();
  const url = window.URL.createObjectURL(new Blob([blob]));

  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  window.URL.revokeObjectURL(url);
}

// #endregion download workbook


// #region generate excel for single scenario estimator
export async function exportSingleScenarioWithTemplate({
  data,
  headers,
  inputSummary,
  manualInputSummary,
  fileName = 'estimator_result.xlsx',
  title = 'Emission Summary',
}) {
  
  const workbook = new ExcelJS.Workbook();

  // --- SHEET: Total Emission Data ---
  const emissionSheet = workbook.addWorksheet('Template_1');
  emissionSheet.name = 'Total Emission Data';
  emissionSheet.getCell('C1').value = `${title} - Total Emission Data`;

  // add logo
  try {
    const imageBuffer = loadImageAsBuffer('/motion2.png');
    if (imageBuffer) {
      const imageId1 = workbook.addImage({
        buffer: imageBuffer,
        extension: 'png',
      });
      emissionSheet.addImage(imageId1, 'A1:A1');
    } else {
      console.warn('Could not load logo image from /motion.png');
    }
  } catch (error) {
    console.warn('Could not add logo to Excel:', error);
  }

  // --- MULTILEVEL HEADER ROWS ---
  const startRow = 2; // header starts at row 2
  const headerRows = [["", "Year"], ["", "Location"]];
  
  headers.forEach(header => {
    if (header.children) {
      header.children.forEach(child => {
        headerRows.push([header.label, child.label]);
      });
    } else {
      headerRows.push([header.label]);
    }
  });

  // Flatten and write headers into ExcelJS
  const maxDepth = Math.max(...headerRows.map(row => row.length));
  const finalHeaderRows = Array.from({ length: maxDepth }, () => []);

  headerRows.forEach(row => {
    row.forEach((cell, i) => {
      finalHeaderRows[i].push(cell);
    });
  });

  finalHeaderRows.forEach((row, rowIndex) => {
    const excelRow = emissionSheet.getRow(startRow + rowIndex);
    row.forEach((cell, colIndex) => {
      const Total_emission_header_cell = excelRow.getCell(colIndex + 1);
      Total_emission_header_cell.value = cell || "";

      // Header cell styling
      Total_emission_header_cell.alignment = { 
        vertical: 'middle', 
        horizontal: 'center', 
        wrapText: false 
      };
      Total_emission_header_cell.font = {
        name: 'Calibri', // Font family
        family: 4, // Font family
        size: 12, // Font size
        bold: true, // Bold
        italic: false, // Italic
        underline: false, // Underline
        color: { argb: '0D3937' } // Text color
      };
      Total_emission_header_cell.fill = { 
        type: 'pattern', 
        pattern: 'solid', 
        fgColor: { argb: 'A9D08E' } };
      Total_emission_header_cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
  
    });
    excelRow.commit();
  });

  // --- DATA ROWS ---
  const dataStartRow = startRow + finalHeaderRows.length;
  data.forEach((rowData, rowIndex) => {
    const row = emissionSheet.getRow(dataStartRow + rowIndex);

    // Determine fill color based on even/odd row
    const isEvenRow = (rowIndex + 1) % 2 === 0;
    const fillColor = isEvenRow ? 'E2EFDA' : 'FFFFFF';

    Object.values(rowData).forEach((val, colIndex) => {
      const Total_emission_data_cell = row.getCell(colIndex + 1);
      Total_emission_data_cell.value = val;

      // Apply fill color to the data cell
      Total_emission_data_cell.font = {
        name: 'Calibri', // Font family
        family: 4, // Font family
        size: 11, // Font size
        bold: false, // Bold
        italic: false, // Italic
        underline: false, // Underline
        color: { argb: '00000000' } // Text color
      };
      Total_emission_data_cell.alignment = { 
        vertical: 'middle', 
        horizontal: 'left', 
        wrapText: false 
      };
      Total_emission_data_cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fillColor } };
      Total_emission_data_cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };

      

    });
    row.commit();
  });

  // console.log("finalHeaderRows", finalHeaderRows);
  
  // Merge cells for the Title row
  const emission_lastColumnLetter = columnIndexToLetter(finalHeaderRows[1].length);
  // console.log("emission_lastColumnLetter", emission_lastColumnLetter);
  
  const emission_mergeEnd = `${emission_lastColumnLetter}1`;
  // console.log("emission_mergeEnd", emission_mergeEnd);
  
  emissionSheet.mergeCells('C1:' + emission_mergeEnd);
  emissionSheet.getCell('C1').alignment = { vertical: 'middle', horizontal: 'left' };
  emissionSheet.getCell('C1').font = {
    name: 'Calibri',
    color: { argb: 'FFFFFFFF' },
    family: 4,
    size: 36,
    underline: false,
    italic: false,
    bold: true
  };
  emissionSheet.getCell('A1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '141722' } };
  emissionSheet.getCell('B1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '141722' } };
  emissionSheet.getCell('C1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '141722' } };

  // --- SHEET: Input Summary ---
  const inputSheet = workbook.addWorksheet('Template_2');
  inputSheet.name = 'Input Summary';
  inputSheet.getCell('C1').value = `${title} - Input Summary`;

  // add logo
  try {
    const imageBuffer = loadImageAsBuffer('/motion2.png');
    if (imageBuffer) {
      const imageId1 = workbook.addImage({
        buffer: imageBuffer,
        extension: 'png',
      });
      inputSheet.addImage(imageId1, 'A1:A1');
    } else {
      console.warn('Could not load logo image from /motion.png');
    }
  } catch (error) {
    console.warn('Could not add logo to Excel:', error);
  }

  const inputHeader = Object.keys(inputSummary[0] || {});
  inputHeader.forEach((key, i) => {
    const input_header_cell = inputSheet.getRow(2).getCell(i + 1);
    input_header_cell.value = key;

    // Header cell styling
    input_header_cell.alignment = { 
      vertical: 'middle', 
      horizontal: 'center', 
      wrapText: false 
    };
    input_header_cell.font = {
      name: 'Calibri', // Font family
      family: 4, // Font family
      size: 12, // Font size
      bold: true, // Bold
      italic: false, // Italic
      underline: false, // Underline
      color: { argb: '0D3937' } // Text color
    };
    input_header_cell.fill = { 
      type: 'pattern', 
      pattern: 'solid', 
      fgColor: { argb: 'A9D08E' } };
    input_header_cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    };
    
  });



  inputSummary.forEach((rowData, rowIndex) => {
    const row = inputSheet.getRow(3 + rowIndex);

    // Determine fill color based on even/odd row
    const isEvenRow = (rowIndex + 1) % 2 === 0;
    const fillColor = isEvenRow ? 'E2EFDA' : 'FFFFFF';

    inputHeader.forEach((key, colIndex) => {
      const input_data_cell = row.getCell(colIndex + 1);
      input_data_cell.value = rowData[key];

      // Apply fill color to the data cell
      input_data_cell.font = {
        name: 'Calibri', // Font family
        family: 4, // Font family
        size: 11, // Font size
        bold: false, // Bold
        italic: false, // Italic
        underline: false, // Underline
        color: { argb: '00000000' } // Text color
      };
      input_data_cell.alignment = { 
        vertical: 'middle', 
        horizontal: 'left', 
        wrapText: false 
      };
      input_data_cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fillColor } };
      input_data_cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };

      

    });
    row.commit();
  });

    // Merge cells for the Title row
    const input_lastColumnLetter = columnIndexToLetter(inputHeader.length);
    const input_mergeEnd = `${input_lastColumnLetter}1`;
    inputSheet.mergeCells('C1:' + input_mergeEnd);
    inputSheet.getCell('C1').alignment = { vertical: 'middle', horizontal: 'left' };
    inputSheet.getCell('C1').font = {
      name: 'Calibri',
      color: { argb: 'FFFFFFFF' },
      family: 4,
      size: 36,
      underline: false,
      italic: false,
      bold: true
    };
    inputSheet.getCell('A1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '141722' } };
    inputSheet.getCell('B1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '141722' } };
    inputSheet.getCell('C1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '141722' } };

  // --- SHEET: Manual Input Summary ---
  const manualSheet = workbook.addWorksheet('Template_3');



  const manualHeader = Object.keys(manualInputSummary[0] || {});
  if (manualHeader.length === 0) {
    console.log("manualInputSummary is empty");
  } else {
    manualSheet.name = 'Manual Input Summary';
    manualSheet.getCell('C1').value = `${title} - Manual Input Summary`;

    // add logo
    try {
      const imageBuffer = loadImageAsBuffer('/motion2.png');
      if (imageBuffer) {
        const imageId1 = workbook.addImage({
          buffer: imageBuffer,
          extension: 'png',
        });
        manualSheet.addImage(imageId1, 'A1:A1');
      } else {
        console.warn('Could not load logo image from /motion.png');
      }
    } catch (error) {
      console.warn('Could not add logo to Excel:', error);
    }

    manualHeader.forEach((key, i) => {
      const manual_header_cell = manualSheet.getRow(2).getCell(i + 1);
      manual_header_cell.value = key;

      manual_header_cell.alignment = { 
        vertical: 'middle', 
        horizontal: 'center', 
        wrapText: false 
      };
      manual_header_cell.font = {
        name: 'Calibri', // Font family
        family: 4, // Font family
        size: 12, // Font size
        bold: true, // Bold
        italic: false, // Italic
        underline: false, // Underline
        color: { argb: '0D3937' } // Text color
      };
      manual_header_cell.fill = { 
        type: 'pattern', 
        pattern: 'solid', 
        fgColor: { argb: 'A9D08E' } };
      manual_header_cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
  
    });

    manualInputSummary.forEach((rowData, rowIndex) => {
      const row = manualSheet.getRow(3 + rowIndex);

          // Determine fill color based on even/odd row
    const isEvenRow = (rowIndex + 1) % 2 === 0;
    const fillColor = isEvenRow ? 'E2EFDA' : 'FFFFFF';
      manualHeader.forEach((key, colIndex) => {
        const manual_data_cell = row.getCell(colIndex + 1);
        manual_data_cell.value = rowData[key];

        manual_data_cell.font = {
          name: 'Calibri', // Font family
          family: 4, // Font family
          size: 11, // Font size
          bold: false, // Bold
          italic: false, // Italic
          underline: false, // Underline
          color: { argb: '00000000' } // Text color
        };
        manual_data_cell.alignment = { 
          vertical: 'middle', 
          horizontal: 'left', 
          wrapText: false 
        };
        manual_data_cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fillColor } };
        manual_data_cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
  
        
      });
      row.commit();
    });

    // Merge cells for the Title row
    const manual_lastColumnLetter = columnIndexToLetter(manualHeader.length);
    const manual_mergeEnd = `${manual_lastColumnLetter}1`;
    manualSheet.mergeCells('C1:' + manual_mergeEnd);
    manualSheet.getCell('C1').alignment = { vertical: 'middle', horizontal: 'left' };
    manualSheet.getCell('C1').font = {
      name: 'Calibri',
      color: { argb: 'FFFFFFFF' },
      family: 4,
      size: 36,
      underline: false,
      italic: false,
      bold: true
    };
    manualSheet.getCell('A1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '141722' } };
    manualSheet.getCell('B1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '141722' } };
    manualSheet.getCell('C1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '141722' } };
  }

  // Remove unused template sheets
  workbook.worksheets
    .filter(ws => ws.name.startsWith("Template_") && !['Total Emission Data', 'Input Summary', 'Manual Input Summary'].includes(ws.name))
    .forEach(ws => workbook.removeWorksheet(ws.id));

  // Save file
  await downloadWorkbook(workbook, fileName);
}

// #endregion generate excel for single scenario estimator


// #region generate excel for all scenarios with template 
export async function exportAllScenariosWithTemplate({
  totalData,
  totalDataHeaders,
  projectData,
  projectHeaders,
  baselineData,
  baselineHeaders,
  abatementData,
  abatementHeaders,
  inputSummary,
  manualInputSummary,
  fileName = 'estimator_scenarios_result.xlsx',
}) {
  const workbook = new ExcelJS.Workbook();

  const addSheetWithData = (sheetName, templateSheetName, headers, data, extraHeader = []) => {
    const sheet = workbook.addWorksheet(templateSheetName);

    sheet.name = sheetName;
    sheet.getCell('C1').value = `Emission Summary - ${sheetName}`;

    // add logo
    try {
      const imageBuffer = loadImageAsBuffer('/motion2.png');
      if (imageBuffer) {
        const imageId1 = workbook.addImage({
          buffer: imageBuffer,
          extension: 'png',
        });
        sheet.addImage(imageId1, 'A1:A1');
      } else {
        console.warn('Could not load logo image from /motion.png');
      }
    } catch (error) {
      console.warn('Could not add logo to Excel:', error);
    }

    // --- Headers ---
    const headerRows = [...extraHeader];
    headers.forEach(header => {
      if (header.children) {
        header.children.forEach(child => {
          if (child.children) {
            child.children.forEach(grandchild => {
              headerRows.push([header.label, child.label, grandchild.label]);
            });
          } else {
            headerRows.push([header.label, child.label]);
          }
        });
      } else {
        headerRows.push([header.label]);
      }
    });

    const maxDepth = Math.max(...headerRows.map(r => r.length));
    const finalHeaderRows = Array.from({ length: maxDepth }, () => []);
    headerRows.forEach(row => {
      row.forEach((cell, i) => {
        finalHeaderRows[i].push(cell);
      });
    });

    finalHeaderRows.forEach((row, rowIndex) => {
      const excelRow = sheet.getRow(2 + rowIndex);
      row.forEach((cell, colIndex) => {
        const headerCell = excelRow.getCell(colIndex + 1);
        headerCell.value = cell || "";

        // Header cell styling
        headerCell.alignment = { 
          vertical: 'middle', 
          horizontal: 'center', 
          wrapText: false 
        };
        headerCell.font = {
          name: 'Calibri', // Font family
          family: 4, // Font family
          size: 12, // Font size
          bold: true, // Bold
          italic: false, // Italic
          underline: false, // Underline
          color: { argb: '0D3937' } // Text color
        };
        headerCell.fill = { 
          type: 'pattern', 
          pattern: 'solid', 
          fgColor: { argb: 'A9D08E' } };
        headerCell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };



      });
      excelRow.commit();
    });

    // --- Data ---
    const dataStartRow = 2 + finalHeaderRows.length;

    if (sheetName === "Abatement") {
      console.log("headers", headers);
      const lastRowHeaders = [];
      
      // Recursively extract all prop values from nested headers
      const extractProps = (items) => {
        items.forEach(item => {
          if (item.prop) {
            lastRowHeaders.push(item.prop);
          }
          if (item.children && item.children.length > 0) {
            extractProps(item.children);
          }
        });
      };
      
      extractProps(headers);
      console.log("lastRowHeaders", lastRowHeaders);
      
      data.forEach((rowData, rowIndex) => {
        const row = sheet.getRow(dataStartRow + rowIndex);
        if (rowData["Year"] === "Total") {
          // console.log("rowData", rowData);
          const filteredData = {};
          Object.entries(rowData).forEach(([key, value]) => {
            // Always preserve the "Year" field when it contains "Total"
            if (key === "Year" || lastRowHeaders.includes(key)) {
              filteredData[key] = value;
            }
          });
          // console.log("filteredData", filteredData);
          rowData = filteredData;
        }

        // Determine fill color based on even/odd row
        const isEvenRow = (rowIndex + 1) % 2 === 0;
        const abatement_fillColor = isEvenRow ? 'E2EFDA' : 'FFFFFF';

        Object.values(rowData).forEach((val, colIndex) => {
          const abatement_data_cell = row.getCell(colIndex + 1);
          // row.getCell(colIndex + 1).value = val;
          abatement_data_cell.value = val;

          // Apply fill color to the data cell
          abatement_data_cell.font = {
            name: 'Calibri', // Font family
            family: 4, // Font family
            size: 11, // Font size
            bold: false, // Bold
            italic: false, // Italic
            underline: false, // Underline
            color: { argb: '00000000' } // Text color
          };
          abatement_data_cell.alignment = { 
            vertical: 'middle', 
            horizontal: 'left', 
            wrapText: false 
          };
          abatement_data_cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: abatement_fillColor } };
          abatement_data_cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };

        });
        row.commit();
      });

        // Merge cells for the Title row
      const abatement_lastColumnLetter = columnIndexToLetter(finalHeaderRows[1].length);
      console.log("abatement_lastColumnLetter", abatement_lastColumnLetter);
      
      const abatement_mergeEnd = `${abatement_lastColumnLetter}1`;
      console.log("abatement_mergeEnd", abatement_mergeEnd);
      
      sheet.mergeCells('C1:' + abatement_mergeEnd);
      sheet.getCell('C1').alignment = { vertical: 'middle', horizontal: 'left' };
      sheet.getCell('C1').font = {
        name: 'Calibri',
        color: { argb: 'FFFFFFFF' },
        family: 4,
        size: 36,
        underline: false,
        italic: false,
        bold: true
      };
      sheet.getCell('A1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '141722' } };
      sheet.getCell('B1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '141722' } };
      sheet.getCell('C1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '141722' } };
      
    }
    else {
      data.forEach((rowData, rowIndex) => {
        const row = sheet.getRow(dataStartRow + rowIndex);

        // Determine fill color based on even/odd row
        const isEvenRow = (rowIndex + 1) % 2 === 0;
        const data_fillColor = isEvenRow ? 'E2EFDA' : 'FFFFFF';

        Object.values(rowData).forEach((val, colIndex) => {
          const dataCell = row.getCell(colIndex + 1);
          dataCell.value = val;

          // Apply fill color to the data cell
          dataCell.font = {
            name: 'Calibri', // Font family
            family: 4, // Font family
            size: 11, // Font size
            bold: false, // Bold
            italic: false, // Italic
            underline: false, // Underline
            color: { argb: '00000000' } // Text color
          };
          dataCell.alignment = { 
            vertical: 'middle', 
            horizontal: 'left', 
            wrapText: false 
          };
          dataCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: data_fillColor } };
          dataCell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };

        });
        row.commit();
      });

      // Merge cells for the Title row
      const data_lastColumnLetter = columnIndexToLetter(finalHeaderRows[1].length);
      console.log("data_lastColumnLetter", data_lastColumnLetter);
      
      const data_mergeEnd = `${data_lastColumnLetter}1`;
      console.log("data_mergeEnd", data_mergeEnd);
      
      sheet.mergeCells('C1:' + data_mergeEnd);
      sheet.getCell('C1').alignment = { vertical: 'middle', horizontal: 'left' };
      sheet.getCell('C1').font = {
        name: 'Calibri',
        color: { argb: 'FFFFFFFF' },
        family: 4,
        size: 36,
        underline: false,
        italic: false,
        bold: true
      };
      sheet.getCell('A1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '141722' } };
      sheet.getCell('B1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '141722' } };
      sheet.getCell('C1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '141722' } };
    }

  };

  // Add all data sheets
  addSheetWithData('Scenario_Total', 'Template_1', totalDataHeaders, totalData, [["", "Scenario Name"]]);
  addSheetWithData('Project_Emission', 'Template_2', projectHeaders, projectData, [["", "Year"]]);
  addSheetWithData('Baseline_Emission', 'Template_3', baselineHeaders, baselineData, [["", "Year"]]);
  addSheetWithData('Abatement', 'Template_4', abatementHeaders, abatementData, [["", "", "Year"]]);
  // console.log("totalData", totalData);
  // console.log("totalDataHeaders", totalDataHeaders);
  // console.log("projectData", projectData);
  // console.log("baselineData", baselineData);
  // console.log("abatementData", abatementData);
  // console.log("projectHeaders", projectHeaders);
  // console.log("baselineHeaders", baselineHeaders);
  // console.log("abatementHeaders", abatementHeaders);

  // Input Summary Sheet
  if (inputSummary && inputSummary.length > 0) {
  const inputSheet = workbook.addWorksheet('Template_5');
  inputSheet.name = 'Input Summary';
  inputSheet.getCell('C1').value = `Emission Summary - Input Summary`;

    // add logo
    try {
      const imageBuffer = loadImageAsBuffer('/motion2.png');
      if (imageBuffer) {
        const imageId1 = workbook.addImage({
          buffer: imageBuffer,
          extension: 'png',
        });
        inputSheet.addImage(imageId1, 'A1:A1');
      } else {
        console.warn('Could not load logo image from /motion.png');
      }
    } catch (error) {
      console.warn('Could not add logo to Excel:', error);
    }

  const inputHeader = Object.keys(inputSummary[0] || {});
  inputHeader.forEach((key, i) => {
    const input_header_cell = inputSheet.getRow(2).getCell(i + 1);
    input_header_cell.value = key;

    // Header cell styling
      input_header_cell.alignment = { 
        vertical: 'middle', 
        horizontal: 'center', 
        wrapText: false 
      };
      input_header_cell.font = {
        name: 'Calibri', // Font family
        family: 4, // Font family
        size: 12, // Font size
        bold: true, // Bold
        italic: false, // Italic
        underline: false, // Underline
        color: { argb: '0D3937' } // Text color
      };
      input_header_cell.fill = { 
        type: 'pattern', 
        pattern: 'solid', 
        fgColor: { argb: 'A9D08E' } };
      input_header_cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };

    

  });
  inputSummary.forEach((rowData, rowIndex) => {
    const row = inputSheet.getRow(3 + rowIndex);

    // Determine fill color based on even/odd row
    const isEvenRow = (rowIndex + 1) % 2 === 0;
    const input_fillColor = isEvenRow ? 'E2EFDA' : 'FFFFFF';

    inputHeader.forEach((key, colIndex) => {
      const input_data_cell = row.getCell(colIndex + 1);
      input_data_cell.value = rowData[key];

      // Apply fill color to the data cell
      input_data_cell.font = {
        name: 'Calibri', // Font family
        family: 4, // Font family
        size: 11, // Font size
        bold: false, // Bold
        italic: false, // Italic
        underline: false, // Underline
        color: { argb: '00000000' } // Text color
      };
      input_data_cell.alignment = { 
        vertical: 'middle', 
        horizontal: 'left', 
        wrapText: false 
      };
      input_data_cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb:input_fillColor } };
      input_data_cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
      
    });
    row.commit();
  });

    // Merge cells for the Title row
    const input_lastColumnLetter = columnIndexToLetter(inputHeader.length);
    // console.log("input_lastColumnLetter", input_lastColumnLetter);
    
    const input_mergeEnd = `${input_lastColumnLetter}1`;
    // console.log("input_mergeEnd", input_mergeEnd);
    
    inputSheet.mergeCells('C1:' + input_mergeEnd);
    inputSheet.getCell('C1').alignment = { vertical: 'middle', horizontal: 'left' };
    inputSheet.getCell('C1').font = {
      name: 'Calibri',
      color: { argb: 'FFFFFFFF' },
      family: 4,
      size: 36,
      underline: false,
      italic: false,
      bold: true
    };
    inputSheet.getCell('A1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '141722' } };
    inputSheet.getCell('B1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '141722' } };
    inputSheet.getCell('C1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '141722' } };
  } 
  else {
    console.log("inputSummary is empty, skip input summary sheet");
  }

  if (manualInputSummary && manualInputSummary.length > 0) {
  // Manual Input Summary Sheet
  const manualSheet = workbook.addWorksheet('Template_6');
  const manualHeader = Object.keys(manualInputSummary[0] || {});
  if (manualHeader.length === 0) {
    console.log("manualInputSummary is empty");
  } else {
    manualSheet.name = 'Manual Input Summary';
    manualSheet.getCell('C1').value = `Emission Summary - Manual Input Summary`;

    // add logo
    try {
      const imageBuffer = loadImageAsBuffer('/motion2.png');
      if (imageBuffer) {
        const imageId1 = workbook.addImage({
          buffer: imageBuffer,
          extension: 'png',
        });
        manualSheet.addImage(imageId1, 'A1:A1');
      } else {
        console.warn('Could not load logo image from /motion.png');
      }
    } catch (error) {
      console.warn('Could not add logo to Excel:', error);
    }

    manualHeader.forEach((key, i) => {
      const manual_header_cell = manualSheet.getRow(2).getCell(i + 1);
      manual_header_cell.value = key;

      // Header cell styling
      manual_header_cell.alignment = { 
        vertical: 'middle', 
        horizontal: 'center', 
        wrapText: false 
      };
      manual_header_cell.font = {
        name: 'Calibri', // Font family
        family: 4, // Font family
        size: 12, // Font size
        bold: true, // Bold
        italic: false, // Italic
        underline: false, // Underline
        color: { argb: '0D3937' } // Text color
      };
      manual_header_cell.fill = { 
        type: 'pattern', 
        pattern: 'solid', 
        fgColor: { argb: 'A9D08E' } };
      manual_header_cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };


      

    });
    // console.log("manualInputSummary", manualInputSummary);
    // console.log("manualHeader", manualHeader);
    manualInputSummary.forEach((rowData, rowIndex) => {
      const row = manualSheet.getRow(3 + rowIndex);

            // Determine fill color based on even/odd row
      const isEvenRow = (rowIndex + 1) % 2 === 0;
      const manual_fillColor = isEvenRow ? 'E2EFDA' : 'FFFFFF';
      manualHeader.forEach((key, colIndex) => {

        const manual_data_cell = row.getCell(colIndex + 1);
        manual_data_cell.value = rowData[key];

      // Apply fill color to the data cell
      manual_data_cell.font = {
        name: 'Calibri', // Font family
        family: 4, // Font family
        size: 11, // Font size
        bold: false, // Bold
        italic: false, // Italic
        underline: false, // Underline
        color: { argb: '00000000' } // Text color
      };
      manual_data_cell.alignment = { 
        vertical: 'middle', 
        horizontal: 'left', 
        wrapText: false 
      };
      manual_data_cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: manual_fillColor } };
      manual_data_cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };

      });
      row.commit();
    });

      // Merge cells for the Title row
  const manual_lastColumnLetter = columnIndexToLetter(manualHeader.length);
  // console.log("manual_lastColumnLetter", manual_lastColumnLetter);
  
  const manual_mergeEnd = `${manual_lastColumnLetter}1`;
  // console.log("manual_mergeEnd", manual_mergeEnd);
  
  manualSheet.mergeCells('C1:' + manual_mergeEnd);
  manualSheet.getCell('C1').alignment = { vertical: 'middle', horizontal: 'left' };
  manualSheet.getCell('C1').font = {
    name: 'Calibri',
    color: { argb: 'FFFFFFFF' },
    family: 4,
    size: 36,
    underline: false,
    italic: false,
    bold: true
  };
  manualSheet.getCell('A1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '141722' } };
  manualSheet.getCell('B1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '141722' } };
  manualSheet.getCell('C1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '141722' } };
  }
} else {
  console.log("manualInputSummary is empty, skip manual input summary sheet");
}

  await downloadWorkbook(workbook, fileName);
}

